import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import {
  GameClientEvents,
  GameServerEvents,
  ReadyClientEvents,
  ReadyServerEvents,
  RoomClientEvents,
  ActiveRoomDto,
  RoomErrorDto,
  RoomResumedDto,
  RoomServerEvents,
  SessionConnectErrorData,
  SessionHandshakeAuth,
  SessionRejectReason,
  SessionServerEvents,
  WelcomeDto,
  SocketService,
  SocketConfig,
  ConnectionStatus,
  GameState,
  Direction,
  DirectionInput,
  PlayerInputDto,
  AttackData,
  PowerUpData,
  GameData,
  MatchEndResult,
  MatchStartDto,
  CreateRoomDto,
  JoinRoomDto,
  VerifyRoomPasswordDto,
  LeaveRoomDto,
  RoomStateDto,
  PlayerRoomEventDto,
  GameSettings,
} from '@snake/shared';
import { Observable, Subject, take } from 'rxjs';
import type { Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { ServerClock } from './server-clock';
import { UsernameStorageService } from './username-storage.service';

const TOKEN_KEY = 'snk_player_token';

/**
 * Where this tab's player session stands.
 * - `idle`: not connected (never connected, or closed on purpose)
 * - `connecting`: handshake or automatic reconnection in progress
 * - `active`: identified by the server (WELCOME received)
 * - `conflict`: refused — the same session is live in another tab
 * - `replaced`: another tab took the session over; this one stays offline
 */
export type SessionState =
  | 'idle'
  | 'connecting'
  | 'active'
  | 'conflict'
  | 'replaced';

@Injectable({
  providedIn: 'root',
})
export class GameSocketService implements OnDestroy {
  private socketService: SocketService;
  private usernameStorage = inject(UsernameStorageService);

  /** Session token, kept in memory once read; localStorage shares it across tabs. */
  private token: string | null = readStoredToken();
  /** Set for the one handshake that should take the session over. */
  private takeoverRequested = false;
  /**
   * Server timestamps are converted to this browser's clock as they arrive,
   * so everything past this service compares them with `Date.now()`.
   */
  private readonly clock = new ServerClock();

  private readonly sessionStateSignal = signal<SessionState>('idle');
  /** Where this tab's player session stands (see SessionState). */
  readonly sessionState = this.sessionStateSignal.asReadonly();
  private readonly playerIdSignal = signal<string | null>(null);
  /** This player's stable id — set from WELCOME, unchanged across reconnects. */
  readonly playerId = this.playerIdSignal.asReadonly();

  private identifiedSubject = new Subject<WelcomeDto>();
  private reconnectExhaustedSubject = new Subject<void>();

  // Observable subjects for server events
  private gameStateUpdatesSubject = new Subject<GameState>();
  private serverTickSubject = new Subject<GameState>();
  private matchTimerSubject = new Subject<number>();
  private playerReadySubject = new Subject<{
    playerId: string;
    isReady: boolean;
  }>();
  private playerJoinedSubject = new Subject<PlayerRoomEventDto>();
  private playerLeftSubject = new Subject<PlayerRoomEventDto>();
  private attackIncomingSubject = new Subject<AttackData>();
  private powerUpUsedSubject = new Subject<PowerUpData>();
  private gameStartSubject = new Subject<GameData>();
  private gameOverSubject = new Subject<MatchEndResult>();
  private roomCreatedSubject = new Subject<RoomStateDto>();
  private roomJoinedSubject = new Subject<RoomStateDto>();
  private roomErrorSubject = new Subject<RoomErrorDto>();
  private roomsUpdatedSubject = new Subject<RoomStateDto[]>();
  private activeRoomSubject = new Subject<ActiveRoomDto | null>();
  private readyUpdateSubject = new Subject<{
    readyPlayers: Record<string, boolean>;
    allReady?: boolean;
  }>();
  private countdownStartSubject = new Subject<{ startsAt: number }>();
  private matchStartSubject = new Subject<MatchStartDto>();
  private rematchWaitingSubject = new Subject<{ requesters: string[] }>();
  private targetUpdateSubject = new Subject<{
    playerId: string;
    targetPlayerId: string;
  }>();
  private playerDisconnectedSubject = new Subject<PlayerRoomEventDto>();
  private playerAwaySubject = new Subject<PlayerRoomEventDto>();
  private playerReturnedSubject = new Subject<PlayerRoomEventDto>();
  private roomResumedSubject = new Subject<RoomResumedDto>();
  private countdownCancelledSubject = new Subject<{
    roomId: string;
    room?: RoomStateDto;
  }>();

  constructor() {
    const config: SocketConfig = {
      url: environment.wsUrl,
      namespace: 'game',
      options: {
        transports: ['websocket', 'polling'],
        timeout: 20000,
      },
      auth: (cb) => cb(this.buildHandshakeAuth()),
    };

    this.socketService = new SocketService(config);
    // One socket for the service's lifetime: listeners are registered once and
    // survive every reconnect.
    const socket = this.socketService.getOrCreateSocket();
    this.registerSessionListeners(socket);
    this.setupEventListeners(socket);

    // Close the socket explicitly when the page goes away, so the server sees
    // the disconnect at once and a reload is not mistaken for a second tab.
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
  }

  // ===== CONNECTION METHODS =====

  /**
   * Connect and wait until the server has identified this player (WELCOME).
   * Rejects when the handshake is refused (`sessionState()` tells why) or the
   * server cannot be reached. Resolves with the WELCOME, or null when the
   * session was already active.
   */
  async connect(): Promise<WelcomeDto | null> {
    if (this.sessionState() === 'active' && this.socketService.isConnected()) {
      return null;
    }
    // Subscribe before connecting: WELCOME can arrive in the same tick as the
    // connect event.
    const identified = new Promise<WelcomeDto>((resolve) => {
      this.identifiedSubject.pipe(take(1)).subscribe(resolve);
    });
    this.sessionStateSignal.set('connecting');
    try {
      await this.socketService.connect();
    } catch (error) {
      if (this.sessionState() === 'connecting') {
        this.sessionStateSignal.set('idle');
      }
      throw error;
    }
    return identified;
  }

  /**
   * Take this player's session over from the other tab (or reclaim it after it
   * moved away). The other tab is told and goes offline.
   */
  async takeOver(): Promise<WelcomeDto | null> {
    this.takeoverRequested = true;
    try {
      return await this.connect();
    } finally {
      this.takeoverRequested = false;
    }
  }

  disconnect(): void {
    this.socketService.disconnect();
    this.sessionStateSignal.set('idle');
  }

  isConnected(): boolean {
    return this.socketService.isConnected();
  }

  /** Credentials for the handshake; read on every (re)connection. */
  private buildHandshakeAuth(): SessionHandshakeAuth {
    const username = this.usernameStorage.getUsername();
    return {
      ...(this.token ? { token: this.token } : {}),
      ...(username ? { username } : {}),
      ...(this.takeoverRequested ? { takeover: true } : {}),
    };
  }

  private registerSessionListeners(socket: Socket): void {
    socket.on(RoomServerEvents.WELCOME, (welcome: WelcomeDto) => {
      console.log('🎉 Welcome from server:', welcome);
      this.clock.sync(welcome.serverTime);
      if (welcome.issuedToken) this.storeIssuedToken(welcome.issuedToken);
      this.takeoverRequested = false;
      this.playerIdSignal.set(welcome.playerId);
      this.sessionStateSignal.set('active');
      this.identifiedSubject.next(welcome);
    });

    // Liveness probe: answering it tells the server this tab really is open.
    socket.on(SessionServerEvents.PING_ALIVE, (ack?: () => void) => ack?.());

    socket.on(SessionServerEvents.SESSION_REPLACED, () => {
      console.log('🔁 Session taken over by another tab');
      this.sessionStateSignal.set('replaced');
    });

    socket.on('connect_error', (error: Error & { data?: unknown }) => {
      const data = error.data as Partial<SessionConnectErrorData> | undefined;
      if (data?.reason === SessionRejectReason.ALREADY_CONNECTED) {
        this.sessionStateSignal.set('conflict');
      }
    });

    socket.on('disconnect', (reason: string) => {
      // After a takeover the server closes us on purpose: stay offline.
      if (this.sessionState() === 'replaced') return;
      // socket.io retries a dropped transport by itself (`active` stays true).
      this.sessionStateSignal.set(socket.active ? 'connecting' : 'idle');
      console.log('🔌 Disconnected:', reason);
    });

    socket.io.on('reconnect_failed', () => {
      this.sessionStateSignal.set('idle');
      this.reconnectExhaustedSubject.next();
    });
  }

  /**
   * Persist a newly issued token. If another tab stored a different token in
   * the meantime it wins the shared slot, and this tab keeps its own in memory
   * (two anonymous players in one browser — rare, and harmless).
   */
  private storeIssuedToken(issued: string): void {
    const stored = readStoredToken();
    if (stored === null || stored === this.token) {
      try {
        localStorage.setItem(TOKEN_KEY, issued);
      } catch {
        // Storage unavailable (private mode): the session lasts for this tab only.
      }
    }
    this.token = issued;
  }

  private readonly onPageHide = (): void => {
    if (this.socketService.isConnected()) this.socketService.disconnect();
  };

  private readonly onPageShow = (event: PageTransitionEvent): void => {
    // Restored from the back/forward cache with the socket closed by pagehide.
    if (event.persisted && this.sessionState() === 'active') {
      void this.connect().catch(() => undefined);
    }
  };

  /** Emit startGame event (host only). */
  emitStartGame(roomId: string): void {
    if (!this.socketService.isConnected()) return;
    this.socketService
      .getSocket()
      ?.emit(RoomClientEvents.START_GAME, { roomId });
    console.log('📤 Emitting startGame:', { roomId });
  }

  // ===== CLIENT TO SERVER EMISSIONS =====

  /**
   * Join a game room
   */
  joinGame(gameId: string, playerId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot join game');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.JOIN_GAME, { gameId, playerId });
    console.log('📤 Emitting joinGame:', { gameId, playerId });
  }

  /**
   * Leave the current game
   */
  leaveGame(): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot leave game');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.LEAVE_GAME);
    console.log('📤 Emitting leaveGame');
  }

  /**
   * Send snake movement input
   */
  moveSnake(direction: Direction): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot move snake');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.MOVE_SNAKE, { direction });
    console.log('📤 Emitting moveSnake:', direction);
  }

  /**
   * Send player input to the server
   * Used for real-time directional input during gameplay
   */
  sendPlayerInput(
    gameId: string,
    playerId: string,
    { direction, sequence, life }: DirectionInput
  ): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot send player input');
      return;
    }

    const socket = this.socketService.getSocket()!;
    const input: PlayerInputDto = {
      gameId,
      playerId,
      direction,
      sequence,
      life,
      clientTimestamp: Date.now(),
    };
    socket.emit(GameClientEvents.PLAYER_INPUT, input);
    console.log('📤 Emitting player_input:', {
      gameId,
      playerId,
      direction,
      sequence,
      life,
    });
  }

  /**
   * Use attack ability — fires one block at the attacker's current head position
   */
  useAttack(gameId: string, playerId: string, targetPlayerId?: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot use attack');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.USE_ATTACK, {
      gameId,
      playerId,
      targetPlayerId,
    });
    console.log('📤 Emitting useAttack:', {
      gameId,
      playerId,
      targetPlayerId,
    });
  }

  emitTargetChanged(roomId: string, targetPlayerId: string): void {
    if (!this.socketService.isConnected()) return;
    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.TARGET_CHANGED, { roomId, targetPlayerId });
  }

  /**
   * Use the active power-up (E). Player-variant power-ups apply to the user;
   * opponent-variant power-ups are sent to the selected target.
   */
  usePowerUp(gameId: string, playerId: string, targetPlayerId?: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot use power-up');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.USE_POWER_UP, {
      gameId,
      playerId,
      targetPlayerId,
    });
    console.log('📤 Emitting usePowerUp:', { gameId, playerId, targetPlayerId });
  }

  /**
   * Switch the active power-up slot (Q).
   */
  switchPowerUp(gameId: string, playerId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot switch power-up');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.SWITCH_POWER_UP, { gameId, playerId });
    console.log('📤 Emitting switchPowerUp:', { gameId, playerId });
  }

  /**
   * Emit player_ready event to server
   */
  emitPlayerReady(roomId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot emit player_ready');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(ReadyClientEvents.PLAYER_READY, { roomId });
    console.log('📤 Emitting player_ready:', { roomId });
  }

  /**
   * Request a rematch
   */
  requestRematch(roomId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot request rematch');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.REMATCH, { roomId });
    console.log('📤 Emitting rematch', { roomId });
  }

  /**
   * Create a new room
   */
  createRoom(
    roomName: string,
    password?: string,
    settings?: Partial<GameSettings>
  ): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot create room');
      return;
    }

    const socket = this.socketService.getSocket()!;
    const payload: CreateRoomDto = {
      roomName,
      password,
      username: this.usernameStorage.getUsername() ?? undefined,
      settings,
    };
    socket.emit(RoomClientEvents.CREATE_ROOM, payload);
    console.log('📤 Emitting createRoom:', payload);
  }

  /**
   * Join a room using its joinCode (capability-based auth).
   * Possessing the joinCode is sufficient authorisation.
   */
  joinRoom(joinCode: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot join room');
      return;
    }

    const socket = this.socketService.getSocket()!;
    const payload: JoinRoomDto = { joinCode };
    socket.emit(RoomClientEvents.JOIN_ROOM, payload);
    console.log('📤 Emitting joinRoom:', payload);
  }

  /**
   * Verify password for a private room.
   * On success the server joins the player and responds with roomJoined (including joinCode).
   */
  verifyRoomPassword(roomId: string, password: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot verify room password');
      return;
    }

    const socket = this.socketService.getSocket()!;
    const payload: VerifyRoomPasswordDto = { roomId, password };
    socket.emit(RoomClientEvents.VERIFY_ROOM_PASSWORD, payload);
    console.log('📤 Emitting verifyRoomPassword:', { roomId });
  }

  /**
   * Request the current list of all rooms (one-time fetch)
   */
  getAllRooms(): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot get rooms');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(RoomClientEvents.GET_ALL_ROOMS);
    console.log('📤 Emitting getAllRooms');
  }

  /**
   * Leave a room
   */
  leaveRoom(roomId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot leave room');
      return;
    }

    const socket = this.socketService.getSocket()!;
    const payload: LeaveRoomDto = { roomId };
    socket.emit(RoomClientEvents.LEAVE_ROOM, payload);
    console.log('📤 Emitting leaveRoom:', payload);
  }

  // ===== SERVER TO CLIENT OBSERVABLES =====

  get connectionStatus$(): Observable<ConnectionStatus> {
    return this.socketService.connectionStatus$;
  }

  get gameStateUpdates$(): Observable<GameState> {
    return this.gameStateUpdatesSubject.asObservable();
  }

  get serverTick$(): Observable<GameState> {
    return this.serverTickSubject.asObservable();
  }

  get matchTimer$(): Observable<number> {
    return this.matchTimerSubject.asObservable();
  }

  get playerReady$(): Observable<{ playerId: string; isReady: boolean }> {
    return this.playerReadySubject.asObservable();
  }

  get playerJoined$(): Observable<PlayerRoomEventDto> {
    return this.playerJoinedSubject.asObservable();
  }

  get playerLeft$(): Observable<PlayerRoomEventDto> {
    return this.playerLeftSubject.asObservable();
  }

  get attackIncoming$(): Observable<AttackData> {
    return this.attackIncomingSubject.asObservable();
  }

  get powerUpUsed$(): Observable<PowerUpData> {
    return this.powerUpUsedSubject.asObservable();
  }

  get gameStart$(): Observable<GameData> {
    return this.gameStartSubject.asObservable();
  }

  get gameOver$(): Observable<MatchEndResult> {
    return this.gameOverSubject.asObservable();
  }

  get roomCreated$(): Observable<RoomStateDto> {
    return this.roomCreatedSubject.asObservable();
  }

  get roomJoined$(): Observable<RoomStateDto> {
    return this.roomJoinedSubject.asObservable();
  }

  get roomError$(): Observable<RoomErrorDto> {
    return this.roomErrorSubject.asObservable();
  }

  /** The room this player still belongs to (or null), sent with every room-list fetch. */
  get activeRoom$(): Observable<ActiveRoomDto | null> {
    return this.activeRoomSubject.asObservable();
  }

  get roomsUpdated$(): Observable<RoomStateDto[]> {
    return this.roomsUpdatedSubject.asObservable();
  }

  get readyUpdate$(): Observable<{
    readyPlayers: Record<string, boolean>;
    allReady?: boolean;
  }> {
    return this.readyUpdateSubject.asObservable();
  }

  get countdownStart$(): Observable<{ startsAt: number }> {
    return this.countdownStartSubject.asObservable();
  }

  get matchStart$(): Observable<MatchStartDto> {
    return this.matchStartSubject.asObservable();
  }

  get targetUpdate$(): Observable<{
    playerId: string;
    targetPlayerId: string;
  }> {
    return this.targetUpdateSubject.asObservable();
  }

  get rematchWaiting$(): Observable<{ requesters: string[] }> {
    return this.rematchWaitingSubject.asObservable();
  }

  get playerDisconnected$(): Observable<PlayerRoomEventDto> {
    return this.playerDisconnectedSubject.asObservable();
  }

  /** A member is on the lobby while still in the room (sent on each of their list fetches). */
  get playerAway$(): Observable<PlayerRoomEventDto> {
    return this.playerAwaySubject.asObservable();
  }

  /** A disconnected or away member has the room page open again. */
  get playerReturned$(): Observable<PlayerRoomEventDto> {
    return this.playerReturnedSubject.asObservable();
  }

  /** Full room snapshot after (re)joining a room this player already belongs to. */
  get roomResumed$(): Observable<RoomResumedDto> {
    return this.roomResumedSubject.asObservable();
  }

  /** Every WELCOME: first connect, automatic reconnect, or takeover. */
  get identified$(): Observable<WelcomeDto> {
    return this.identifiedSubject.asObservable();
  }

  /** socket.io gave up reconnecting after a dropped connection. */
  get reconnectExhausted$(): Observable<void> {
    return this.reconnectExhaustedSubject.asObservable();
  }

  get countdownCancelled$(): Observable<{
    roomId: string;
    room?: RoomStateDto;
  }> {
    return this.countdownCancelledSubject.asObservable();
  }

  // ===== PRIVATE METHODS =====

  private setupEventListeners(socket: Socket): void {
    // Game state updates (high frequency)
    socket.on(GameServerEvents.GAME_STATE_UPDATE, (data: GameState) => {
      console.log('📥 Received gameStateUpdate:', data);
      this.gameStateUpdatesSubject.next(this.clock.localizeGameState(data));
    });

    // Server tick events (game loop updates)
    socket.on(GameServerEvents.SERVER_TICK, (data: GameState) => {
      console.log('📥 Received server_tick:', data);
      this.serverTickSubject.next(this.clock.localizeGameState(data));
    });

    // Match timer updates
    socket.on(
      GameServerEvents.MATCH_TIMER,
      (data: { timeRemaining: number }) => {
        console.log('📥 Received match_timer:', data);
        this.matchTimerSubject.next(data.timeRemaining);
      }
    );

    // Player ready status changes (legacy subject — superseded by readyUpdate$)
    socket.on(
      ReadyClientEvents.PLAYER_READY,
      (data: { playerId: string; isReady: boolean }) => {
        console.log('📥 Received player_ready:', data);
        this.playerReadySubject.next(data);
      }
    );

    // Player management events
    socket.on(GameServerEvents.PLAYER_JOINED, (data: PlayerRoomEventDto) => {
      console.log('📥 Received playerJoined:', data);
      this.playerJoinedSubject.next(data);
    });

    socket.on(GameServerEvents.PLAYER_LEFT, (data: PlayerRoomEventDto) => {
      console.log('📥 Received playerLeft:', data);
      this.playerLeftSubject.next(data);
    });

    // Game events
    socket.on(GameServerEvents.ATTACK_INCOMING, (data: AttackData) => {
      console.log('📥 Received attackIncoming:', data);
      this.attackIncomingSubject.next(data);
    });

    socket.on(GameServerEvents.POWER_UP_USED, (data: PowerUpData) => {
      console.log('📥 Received powerUpUsed:', data);
      this.powerUpUsedSubject.next(data);
    });

    socket.on(GameServerEvents.GAME_START, (data: GameData) => {
      console.log('📥 Received gameStart:', data);
      this.gameStartSubject.next(data);
    });

    socket.on(GameServerEvents.GAME_OVER, (data: MatchEndResult) => {
      console.log('📥 Received gameOver:', data);
      this.gameOverSubject.next(data);
    });

    // Room list events (initial fetch response + server push updates)
    socket.on(RoomServerEvents.ALL_ROOMS, (data: RoomStateDto[]) => {
      console.log('📥 Received allRooms:', data);
      this.roomsUpdatedSubject.next(data);
    });

    socket.on(RoomServerEvents.ROOMS_UPDATED, (data: RoomStateDto[]) => {
      console.log('📥 Received roomsUpdated:', data);
      this.roomsUpdatedSubject.next(data);
    });

    socket.on(RoomServerEvents.ACTIVE_ROOM, (data: ActiveRoomDto | null) => {
      this.activeRoomSubject.next(data);
    });

    // Room events
    socket.on(RoomServerEvents.ROOM_CREATED, (data: RoomStateDto) => {
      console.log('📥 Received roomCreated:', data);
      this.roomCreatedSubject.next(data);
    });

    socket.on(RoomServerEvents.ROOM_JOINED, (data: RoomStateDto) => {
      console.log('📥 Received roomJoined:', data);
      this.roomJoinedSubject.next(data);
    });

    socket.on(RoomServerEvents.ERROR, (data: RoomErrorDto) => {
      console.log('📥 Received room error:', data);
      this.roomErrorSubject.next(data);
    });

    // Ready-up events
    socket.on(
      ReadyServerEvents.READY_UPDATE,
      (data: { readyPlayers: Record<string, boolean> }) => {
        console.log('📥 Received ready_update:', data);
        this.readyUpdateSubject.next(data);
      }
    );

    socket.on(
      ReadyServerEvents.COUNTDOWN_START,
      (data: { startsAt: number }) => {
        console.log('📥 Received countdown_start:', data);
        this.countdownStartSubject.next({
          startsAt: this.clock.toLocal(data.startsAt),
        });
      }
    );

    socket.on(ReadyServerEvents.MATCH_START, (data: MatchStartDto) => {
      console.log('📥 Received match_start', data);
      this.matchStartSubject.next(data);
    });

    socket.on(
      GameServerEvents.TARGET_UPDATE,
      (data: { playerId: string; targetPlayerId: string }) => {
        this.targetUpdateSubject.next(data);
      }
    );

    socket.on(
      GameServerEvents.REMATCH_WAITING,
      (data: { requesters: string[] }) => {
        console.log('📥 Received rematch_waiting:', data);
        this.rematchWaitingSubject.next(data);
      }
    );

    // Presence events: disconnected, away, back on the room page
    socket.on(
      GameServerEvents.PLAYER_DISCONNECTED,
      (data: PlayerRoomEventDto) => {
        console.log('📥 Received playerDisconnected:', data);
        this.playerDisconnectedSubject.next(data);
      }
    );

    socket.on(GameServerEvents.PLAYER_AWAY, (data: PlayerRoomEventDto) => {
      console.log('📥 Received playerAway:', data);
      this.playerAwaySubject.next(data);
    });

    socket.on(GameServerEvents.PLAYER_RETURNED, (data: PlayerRoomEventDto) => {
      console.log('📥 Received playerReturned:', data);
      this.playerReturnedSubject.next(data);
    });

    socket.on(GameServerEvents.ROOM_RESUMED, (data: RoomResumedDto) => {
      console.log('📥 Received roomResumed:', data);
      if (data.countdownStartsAt !== undefined) {
        data.countdownStartsAt = this.clock.toLocal(data.countdownStartsAt);
      }
      if (data.gameState) this.clock.localizeGameState(data.gameState);
      this.roomResumedSubject.next(data);
    });

    socket.on(
      GameServerEvents.COUNTDOWN_CANCELLED,
      (data: { roomId: string }) => {
        console.log('📥 Received countdownCancelled:', data);
        this.countdownCancelledSubject.next(data);
      }
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    // Complete all subjects
    this.gameStateUpdatesSubject.complete();
    this.serverTickSubject.complete();
    this.matchTimerSubject.complete();
    this.playerReadySubject.complete();
    this.playerJoinedSubject.complete();
    this.playerLeftSubject.complete();
    this.attackIncomingSubject.complete();
    this.powerUpUsedSubject.complete();
    this.gameStartSubject.complete();
    this.gameOverSubject.complete();
    this.roomCreatedSubject.complete();
    this.roomJoinedSubject.complete();
    this.roomErrorSubject.complete();
    this.roomsUpdatedSubject.complete();
    this.readyUpdateSubject.complete();
    this.countdownStartSubject.complete();
    this.matchStartSubject.complete();
    this.rematchWaitingSubject.complete();
    this.targetUpdateSubject.complete();
    this.playerDisconnectedSubject.complete();
    this.playerAwaySubject.complete();
    this.playerReturnedSubject.complete();
    this.roomResumedSubject.complete();
    this.identifiedSubject.complete();
    this.reconnectExhaustedSubject.complete();
    this.countdownCancelledSubject.complete();

    // Destroy socket service
    this.socketService.destroy();
  }
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
